import axios from "axios";
import Cookies from 'js-cookie';
import ErrorHandler from './errorHandler';

// Get BASE_URL from environment variables
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Log the current environment for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('API Base URL:', BASE_URL);
  console.log('Environment:', process.env.REACT_APP_ENVIRONMENT || 'development');
}

export const publicRequest = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const userRequest = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    'X-Requested-With': 'XMLHttpRequest',
    "x-access-token": Cookies.get('accessToken') || '',
  },
});

let isRefreshing = false;
let failedRequestsQueue = [];

async function refreshAccessToken() {
  const tokenWithUser = JSON.parse(localStorage.getItem("tokenWithUser"));
  if (!tokenWithUser?.refresh_token) {
    throw new Error("No refresh token available");
  }
  
  try {
    const response = await publicRequest.post('/auth/refresh-token', {
      refresh_token: tokenWithUser.refresh_token
    });
    
    const newAccessToken = response.data.access_token;
    
    // Update both cookies and default headers
    Cookies.set('accessToken', newAccessToken);
    userRequest.defaults.headers.common['x-access-token'] = newAccessToken;
    
    // Also update the specific header key used in the interceptor
    userRequest.defaults.headers['x-access-token'] = newAccessToken;
    
    //console.log('Token refreshed successfully:', newAccessToken);
    return newAccessToken;
  } catch (error) {
    //console.error('Token refresh failed:', error);
    ErrorHandler.handleError(error, {
      showToast: true,
      logToConsole: true,
      onAuthError: () => {
        // Clear tokens and redirect to login
        Cookies.remove('accessToken');
        localStorage.removeItem('tokenWithUser');
        window.location.href = '/signin';
      }
    });
    throw error;
  }
}

// Add response interceptor for error handling
userRequest.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push((newToken) => {
            if (newToken) {
              originalRequest.headers['x-access-token'] = newToken;
              resolve(userRequest(originalRequest));
            } else {
              reject(new Error('Token refresh failed'));
            }
          });
        });
      }

      isRefreshing = true;
      try {
        const newAccessToken = await refreshAccessToken();
        
        // Update the original request with the new token
        originalRequest.headers['x-access-token'] = newAccessToken;
        
        // Retry the original request
        const retriedResponse = await userRequest(originalRequest);

        // Process all queued requests with the new token
        failedRequestsQueue.forEach(callback => {
          callback(newAccessToken);
        });
        failedRequestsQueue = [];
        
        return retriedResponse;
      } catch (refreshError) {
        // If refresh fails, reject all queued requests
        failedRequestsQueue.forEach(callback => {
          callback(null);
        });
        failedRequestsQueue = [];
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other types of errors using our centralized error handler
    ErrorHandler.handleError(error, {
      showToast: true,
      logToConsole: true,
      onAuthError: () => {
        // Clear tokens and redirect to login (only for 401 errors)
        Cookies.remove('accessToken');
        localStorage.removeItem('tokenWithUser');
        window.location.href = '/signin';
      }
    });

    return Promise.reject(error);
  }
);

// Add request interceptor to ensure fresh token on every request
userRequest.interceptors.request.use(
  config => {
    // Always get the latest token from cookies
    const token = Cookies.get('accessToken');
    if (token) {
      config.headers['x-access-token'] = token;
    }
    return config;
  },
  error => {
    ErrorHandler.handleError(error, {
      showToast: true,
      logToConsole: true
    });
    return Promise.reject(error);
  }
);

// Create our Currency formatter.
export const formatter = {
  format: (value) => {
    // If value is NaN, undefined, null, or falsy, return ₦0
    if (isNaN(value) || value === undefined || value === null || value === '') {
      return '₦0';
    }

    // Ensure the value is a number before formatting
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(Number(value));
  },
};

// Get current domain URL
export const getDomainUrl = () => {
  return window.location.origin;
};

/* Debug utility to check token status
export const debugTokenStatus = () => {
  const cookieToken = Cookies.get('accessToken');
  const defaultHeaderToken = userRequest.defaults.headers['x-access-token'];
  
  console.log('Token Debug Info:');
  console.log('- Cookie Token:', cookieToken ? 'Present' : 'Missing');
  console.log('- Default Header Token:', defaultHeaderToken ? 'Present' : 'Missing');
  console.log('- Tokens Match:', cookieToken === defaultHeaderToken);
  
  return {
    cookieToken: !!cookieToken,
    headerToken: !!defaultHeaderToken,
    tokensMatch: cookieToken === defaultHeaderToken
  };
};*/